import {useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Modal, Row} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {getRequest, postRequest} from '../../../../services/base'
import {notifyError} from '../../../../utils/notifications'
import {NavContext} from '../../../../context/global_context'
import {AppsContext, SET_SHOW_APP_POPUP} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'

const AppFormModal = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(AppsContext)
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()
  const [modal, contextHolder] = Modal.useModal()
  // Makes are assigned on the main AT host only; a customer portal shows its own, fixed
  const [makeOptions, setMakeOptions] = useState([])
  const [makesEditable, setMakesEditable] = useState(false)

  const selected = state?.selected_app || {}

  const closeFormPopup = () => {
    dispatch({ type: SET_SHOW_APP_POPUP, data: false })
    form.resetFields()
  }

  const getInitialValues = () => ({
    is_create: !selected?.id,
    id: selected?.id || undefined,
    name: selected?.name || '',
    makes: selected?.makes || [],
    allowed_domain: selected?.allowed_domain || '',
  })

  const fetchMakes = async () => {
    const res = await getRequest(`/api/oauth/make_options/`)
    // Loaded once when the form opens, not a search, so a toast is right here
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({id: 'generic.makesLoadError'}))
      return
    }
    const options = (res?.results || []).map((item) => ({value: item.id, label: item.name}))
    setMakeOptions(options)
    setMakesEditable(!!res?.editable)
    // On a customer portal a new app gets that portal's make
    if (!res?.editable && !selected?.id) {
      form.setFieldsValue({makes: options.map((option) => option.value)})
    }
  }

  useEffect(() => {
    if (!state.show_app_popup) return
    form.setFieldsValue(getInitialValues())
    fetchMakes()
  }, [state.show_app_popup])

  const saveApp = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'apps.save' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'makes' && Array.isArray(value)) {
        value.forEach((make) => formData.append('makes', make))
      } else if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    const res = await postRequest(`/api/oauth/save/`, formData)

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'apps.saveSuccess' }),
        },
      })
      props.getAppsListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'apps.saveError' }),
        },
      })
    }
  }

  const confirmSave = () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'apps.confirmSetup' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'apps.confirmSetupContent' }),
      onOk: () => form.submit(),
      centered: true,
      maskClosable: true,
    })
  }

  return (
    <>
      {contextHolder}

      <Modal
        open={state.show_app_popup}
        title={
          selected?.id
            ? intl.formatMessage(
                { id: 'apps.updateTitle' },
                { name: selected.name }
              )
            : intl.formatMessage({ id: 'apps.createTitle' })
        }
        width="50%"
        className="apps-form-modal"
        centered={true}
        footer={null}
        closable={true}
        onCancel={closeFormPopup}
      >
        <div className="container">
          <Form
            id="app-form"
            name="app-form"
            form={form}
            layout="vertical"
            onFinish={saveApp}
          >
            <InputText name="is_create" hidden={true} isRequired={false} />
            <InputText name="id" hidden={true} isRequired={false} />
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <InputText
                  name="name"
                  label={intl.formatMessage({ id: 'apps.name' })}
                  readOnly={!!selected?.id}
                  isRequired={true}
                />
              </Col>
              <Col span={24}>
                <SelectInput
                  name="makes"
                  label={intl.formatMessage({ id: 'apps.makes' })}
                  options={makeOptions}
                  isMulti={true}
                  isRequired={true}
                  disabled={!makesEditable}
                />
              </Col>
              <Col span={24}>
                <InputText
                  name="allowed_domain"
                  label={intl.formatMessage({ id: 'apps.allowedDomains' })}
                  isRequired={true}
                />
              </Col>
              <Col span={24}>
                <Flex justify="end" className="py-4">
                  <Button onClick={confirmSave} type="primary">
                    {intl.formatMessage({ id: 'apps.save' })}
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </>
  )
}

export default AppFormModal
